import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getModulePermissions } from '../../../utils/permissionHelper';
import { logAuditEvent } from '../../../utils/auditLogger';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { exportToExcel, exportToPDF } from '../../../utils/downloadExportedFiles';
import ProductHomePageSectionGrid from './ProductHomePageSectionGrid';
import ProductHomePageSectionModal from './ProductHomePageSectionModal';
import { buildProductHomePageSectionSavePayload } from './utils/mapProductHomePageSection';
import { getNextDisplayOrder } from './utils/productHomePageSectionHelpers';
import { validateProductHomePageSection } from './productHomePageSectionValidation';
import ProductHomePageSectionModel from './models/ProductHomePageSectionModel';

function ProductHomePageSectionMain(props) {
  const [sectionList, setSectionList] = useState([]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [userAction, setUserAction] = useState('Add');
  const [selectedSection, setSelectedSection] = useState(null);
  const [alert, setAlert] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Home Page Section';

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-product-homepage-section/getAll`);
      if (response.status === 200) {
        setSectionList(response.data?.data || []);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to load home page sections!!', 'Error', setAlert);
    }
  };

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module);
    setUserModulePermission(modulePermissions ? modulePermissions : []);
    fetchSections();
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Home Page Section module');
  }, [props.Module, moduleName]);

  const openAddModal = () => {
    setUserAction('Add');
    const nextSection = new ProductHomePageSectionModel();
    nextSection.DisplayOrder = getNextDisplayOrder(sectionList);
    setSelectedSection(nextSection);
    setIsOpen(true);
    logAuditEvent(moduleName, 'Add Button Click', 'Opened add home page section modal');
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedSection(null);
    setUserAction('Add');
  };

  const onSubmit = async (e, data) => {
    if (e.target.name !== 'Add' && e.target.name !== 'Edit') return;

    const payload = buildProductHomePageSectionSavePayload(data, getStorageValues('userId'));
    if (!validateProductHomePageSection(payload, setAlert)) return;

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product-homepage-section/save`, payload);
      const result = response.data?.data;
      if (result?.success === 1) {
        const isAdd = e.target.name === 'Add';
        CreateAlert(
          result.message || (isAdd ? 'Section added successfully!!' : 'Section updated successfully!!'),
          'Success',
          setAlert,
        );
        await fetchSections();
        closeModal();
      } else {
        CreateAlert(result?.message || 'Failed to save section!!', 'Error', setAlert);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to save section!!', 'Error', setAlert);
    }
  };

  const handleExportExcel = () => {
    const columns = [
      { name: 'Section Name', selector: 'SectionName' },
      { name: 'Display Order', selector: 'DisplayOrder' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToExcel('Home Page Sections', columns, sectionList);
  };

  const handleExportPDF = () => {
    const columns = [
      { name: 'Section Name', selector: 'SectionName' },
      { name: 'Display Order', selector: 'DisplayOrder' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToPDF('Home Page Sections', columns, sectionList);
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onModelOpen={openAddModal}
        onRefresh={fetchSections}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
        showAddButton={userModulePermission.AddModule === 1}
      />
      <ProductHomePageSectionGrid
        SectionData={sectionList}
        fetchSections={fetchSections}
        UserPermissions={userModulePermission}
        moduleName={moduleName}
      />
      <ProductHomePageSectionModal
        show={modalIsOpen}
        UserAction={userAction}
        selectedRow={selectedSection}
        onClose={closeModal}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductHomePageSectionMain;
